/** Snapshot Engine — domain types (Sprint 0.6) */

export type SnapshotStatus = 'ACTIVE' | 'EXPIRED' | 'LOCKED' | 'ARCHIVED'

export type SnapshotValidityDays = 3 | 5 | 7 | 15 | 30

export type SnapshotPermission = 'view' | 'create' | 'edit' | 'delete'

export type SnapshotDiffDirection = 'up' | 'down' | 'same'

export type SnapshotHistoryEvent =
  | 'created'
  | 'duplicated'
  | 'locked'
  | 'unlocked'
  | 'expired'
  | 'imported'
  | 'exported'
  | 'item_updated'
  | 'bulk_updated'
  | 'notes_updated'
  | 'version_created'

export interface SnapshotItem {
  id: string
  snapshotId: string
  productId: string | null
  description: string
  supplier: string | null
  supplierCode: string | null
  supplierLink: string | null
  purchasePrice: number
  pixPrice: number
  cardPrice: number
  installmentPrice: number
  salePrice: number
  operationalCost: number
  tax: number
  profit: number
  margin: number
  quantity: number
  stock: number | null
  currency: string
  searchDate: string | null
  createdAt: string
}

export interface QuoteSnapshot {
  id: string
  quoteId: string
  version: number
  versionLabel: string
  status: SnapshotStatus
  createdBy: string
  createdAt: string
  validUntil: string
  validityDays: SnapshotValidityDays
  lockedPrice: boolean
  notes: string
  customerId: string | null
  customerName: string | null
  quoteCode: string
  quoteTitle: string | null
  discountPercent: number
  discountValue: number
  freight: number
  totals: {
    purchaseTotal: number
    saleTotal: number
    profitTotal: number
    margin: number
  }
  items: SnapshotItem[]
  priceEnginePayload?: unknown
  meta?: Record<string, unknown>
}

export interface SnapshotHistoryEntry {
  id: string
  snapshotId: string
  event: SnapshotHistoryEvent | string
  oldValue: string
  newValue: string
  userId: string
  createdAt: string
}

export interface SnapshotDiffField {
  field: string
  label: string
  oldValue: number | string | null
  newValue: number | string | null
  delta: number | null
  deltaPercent: number | null
  direction: SnapshotDiffDirection
}

export interface SnapshotItemCompare {
  key: string
  description: string
  productId: string | null
  leftItemId: string | null
  rightItemId: string | null
  fields: SnapshotDiffField[]
  hasChanges: boolean
}

export interface SnapshotCompareResult {
  left: QuoteSnapshot
  right: QuoteSnapshot
  items: SnapshotItemCompare[]
  summary: {
    changedItems: number
    unchangedItems: number
    addedItems: number
    removedItems: number
  }
}

export interface SnapshotSearchFilters {
  version?: number | null
  customer?: string
  dateFrom?: string | null
  dateTo?: string | null
  supplier?: string
  product?: string
  quoteId?: string | null
  status?: SnapshotStatus | null
}

export interface SnapshotPermissions {
  view: boolean
  create: boolean
  edit: boolean
  delete: boolean
}

export interface SnapshotExportPayload {
  engine: 'snapshot-engine'
  format: 'json'
  version: 1
  exportedAt: string
  snapshot: QuoteSnapshot
  history: SnapshotHistoryEntry[]
}

export interface CreateSnapshotInput {
  quoteId: string
  quoteCode: string
  quoteTitle: string | null
  customerId: string | null
  customerName: string | null
  validUntilDays: SnapshotValidityDays
  lockedPrice?: boolean
  notes?: string
  createdBy?: string
  discountPercent: number
  discountValue: number
  freight: number
  totals: QuoteSnapshot['totals']
  items: Array<{
    productId: string | null
    description: string
    supplier?: string | null
    supplierCode?: string | null
    supplierLink?: string | null
    purchasePrice: number
    pixPrice?: number
    cardPrice?: number
    installmentPrice?: number
    salePrice: number
    operationalCost: number
    tax: number
    profit: number
    margin: number
    quantity: number
    stock?: number | null
    currency?: string
    searchDate?: string | null
  }>
  priceEnginePayload?: unknown
}

export const SNAPSHOT_VALIDITY_OPTIONS: SnapshotValidityDays[] = [3, 5, 7, 15, 30]

export const DEFAULT_SNAPSHOT_PERMISSIONS: SnapshotPermissions = {
  view: true,
  create: true,
  edit: true,
  delete: true,
}
